import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../sectepe-ui/index';
import quoteService, {
  Quote, QuoteItem, Product, STATUS_LABEL, EINHEITEN, euro, menge, centsAus,
} from '../services/quoteService';
import companyService, { Company } from '../services/companyService';
import auftragService from '../services/auftragService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm, RelatedLinks } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { useUrlParams } from '../vertrieb/useUrlParams';
import { datum, isoTag, fehlerText } from '../vertrieb/format';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { APP_ROLLEN } from '../berechtigungen';
import { RECHTE_JE_ROLLE, rollenAufzaehlung } from '../verwaltung/rollen';

/**
 * Angebote und Leistungskatalog.
 *
 * Angebot -> Auftrag -> Rechnung, alles in Intermal (ADR 0008): hier
 * entsteht das Angebot und sein Lebenslauf; aus dem angenommenen Angebot
 * wird der Auftrag. Ein Angebot oeffnet sich ueber ?angebot=<id> (Ziel aus
 * Akte und Pipeline).
 *
 * Rechte (Paket 2): Angebot anlegen und bearbeiten, Positionen und
 * "Abgelaufene pruefen" brauchen angebot.bearbeiten; Statuswechsel und Auftrag
 * aus Angebot brauchen angebot.versenden. Den Produktkatalog (Artikelstamm)
 * pflegt, wer angebot.versenden ODER erp.bearbeiten hat -- wie die
 * Backend-Ausnahme in quote.routes.ts (Buchhaltung pflegt ihn als
 * Lagerartikel). Mit angebot.lesen bleiben Liste, Detail, PDF und Katalog lesend.
 */

/** Rollen, die den Katalog pflegen duerfen (angebot.versenden oder erp.bearbeiten). */
const KATALOG_ROLLEN = APP_ROLLEN.filter((rolle) =>
  RECHTE_JE_ROLLE[rolle].includes('angebot.versenden') || RECHTE_JE_ROLLE[rolle].includes('erp.bearbeiten'));

const STATUS_REIHENFOLGE = ['draft', 'sent', 'accepted', 'declined', 'expired'];

const QuotesPage: React.FC = () => {
  const { push } = useToast();
  const { confirm, prompt } = useConfirm();
  const { get, set } = useUrlParams();
  const navigate = useNavigate();
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('angebot.bearbeiten');
  const darfVersenden = darf('angebot.versenden');
  const darfKatalog = darfVersenden || darf('erp.bearbeiten');
  const angebotParam = get('angebot') || null;
  const statusFilter = get('status');

  const [angebote, setAngebote] = useState<Quote[]>([]);
  const [offen, setOffen] = useState<Quote | null>(null);
  const [produkte, setProdukte] = useState<Product[]>([]);
  const [firmen, setFirmen] = useState<Company[]>([]);
  const [zeigeKatalog, setZeigeKatalog] = useState(false);
  const [zeigeNeu, setZeigeNeu] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [neuFirma, setNeuFirma] = useState('');
  const [neuTitel, setNeuTitel] = useState('');
  const [pNeu, setPNeu] = useState({ name: '', unit: 'Tag', preis: '', taxRate: '19' });

  const laden = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        quoteService.list(statusFilter ? { status: statusFilter } : undefined),
        quoteService.listProducts(),
      ]);
      setAngebote(a ?? []);
      setProdukte(p ?? []);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Laden fehlgeschlagen'));
    } finally {
      setLaedt(false);
    }
  }, [statusFilter]);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => {
    Promise.resolve().then(() => companyService.list()).then((f) => setFirmen(f ?? [])).catch(() => setFirmen([]));
  }, []);

  // Deep-Link ?angebot=<id>
  useEffect(() => {
    if (!angebotParam) {
      setOffen(null);
      return;
    }
    let aktiv = true;
    Promise.resolve()
      .then(() => quoteService.get(angebotParam))
      .then((q) => { if (aktiv && q) setOffen(q); })
      .catch((e) => { if (aktiv) setFehler(fehlerText(e, 'Angebot konnte nicht geladen werden')); });
    return () => { aktiv = false; };
  }, [angebotParam]);

  const melden = (e: unknown, standard: string) => setFehler(fehlerText(e, standard));

  const oeffnen = (q: Quote) => set('angebot', q.id);
  const schliessen = () => { set('angebot', null, { replace: true }); setOffen(null); };

  const anlegen = async () => {
    try {
      const q = await quoteService.create({
        title: neuTitel.trim() || undefined,
        companyId: neuFirma || undefined,
      });
      setNeuTitel('');
      setZeigeNeu(false);
      push({ message: `Angebot ${q.number} angelegt.`, severity: 'success' });
      await laden();
      set('angebot', q.id);
    } catch (e) { melden(e, 'Angebot konnte nicht angelegt werden'); }
  };

  const feld = async (name: string, wert: any) => {
    if (!offen) return;
    try {
      setOffen(await quoteService.update(offen.id, { [name]: wert } as any));
      await laden();
    } catch (e) { melden(e, 'Speichern fehlgeschlagen'); }
  };

  const positionZu = async (productId?: string) => {
    if (!offen) return;
    try {
      setOffen(await quoteService.addItem(offen.id, productId ? { productId } : { description: 'Neue Position' }));
    } catch (e) { melden(e, 'Position konnte nicht angelegt werden'); }
  };

  const positionAendern = async (it: QuoteItem, daten: Partial<QuoteItem>) => {
    try {
      setOffen(await quoteService.updateItem(it.id, daten));
      await laden();
    } catch (e) { melden(e, 'Änderung fehlgeschlagen'); }
  };

  const positionWeg = async (it: QuoteItem) => {
    try {
      setOffen(await quoteService.removeItem(it.id));
      await laden();
    } catch (e) { melden(e, 'Löschen fehlgeschlagen'); }
  };

  const status = async (neu: string) => {
    if (!offen) return;
    let grund: string | undefined;
    if (neu === 'declined') {
      const antwort = await prompt({
        title: `Angebot ${offen.number} als abgelehnt markieren`,
        label: 'Grund der Ablehnung (optional)',
        multiline: true,
        confirmLabel: 'Abgelehnt',
        tone: 'danger',
      });
      if (antwort === null) return;
      grund = antwort || undefined;
    }
    if (neu === 'accepted') {
      const ok = await confirm({
        title: `Angebot ${offen.number} als angenommen markieren?`,
        message: offen.dealId
          ? `Der Vorgangswert wird dabei auf ${euro(offen.netCents, offen.currency)} gesetzt.`
          : 'Das Angebot gilt danach als angenommen. Daraus kann ein Auftrag entstehen.',
        confirmLabel: 'Angenommen',
      });
      if (!ok) return;
    }
    try {
      setOffen(await quoteService.setStatus(offen.id, neu, grund));
      push({ message: `Angebot ${offen.number}: ${STATUS_LABEL[neu] ?? neu}.`, severity: 'success' });
      await laden();
    } catch (e) { melden(e, 'Status konnte nicht gesetzt werden'); }
  };

  const produktAnlegen = async () => {
    if (!pNeu.name.trim()) return;
    try {
      await quoteService.createProduct({
        name: pNeu.name.trim(),
        unit: pNeu.unit,
        unitPriceCents: centsAus(pNeu.preis),
        taxRate: Number(pNeu.taxRate) || 19,
      });
      setPNeu({ name: '', unit: 'Tag', preis: '', taxRate: '19' });
      setProdukte((await quoteService.listProducts()) ?? []);
      push({ message: 'Leistung im Katalog angelegt.', severity: 'success' });
    } catch (e) { melden(e, 'Leistung konnte nicht angelegt werden'); }
  };

  const corePreisliste = async () => {
    try {
      const r = await quoteService.corePreislisteUebernehmen();
      setProdukte((await quoteService.listProducts()) ?? []);
      push({
        message: `Core-Preisliste übernommen: ${r.angelegt} angelegt, ${r.aktualisiert} aktualisiert, ${r.unveraendert} unverändert.`,
        severity: 'success',
      });
    } catch (e) { melden(e, 'Core-Preisliste konnte nicht übernommen werden'); }
  };

  const produktUmschalten = async (p: Product) => {
    try {
      await quoteService.updateProduct(p.id, { active: !p.active });
      setProdukte((await quoteService.listProducts()) ?? []);
    } catch (e) { melden(e, 'Änderung fehlgeschlagen'); }
  };

  const auftragAnlegen = async () => {
    if (!offen) return;
    try {
      const a = await auftragService.ausAngebot(offen.id);
      push({
        message: (a as { vorhanden?: boolean }).vorhanden ? 'Zu diesem Angebot gibt es schon einen Auftrag.' : 'Auftrag aus Angebot angelegt.',
        severity: 'success',
      });
      navigate(`/auftraege?auftrag=${a.id}`);
    } catch (e) { melden(e, 'Auftrag konnte nicht angelegt werden'); }
  };

  const abgelaufene = async () => {
    try {
      const b = await quoteService.abgelaufene();
      push({
        message: b.abgelaufen ? `${b.abgelaufen} Angebot(e) als abgelaufen markiert.` : 'Kein Angebot ist abgelaufen.',
        severity: 'info',
      });
      await laden();
    } catch (e) { melden(e, 'Nachtrag fehlgeschlagen'); }
  };

  const zaehle = (st: string) => angebote.filter((q) => q.status === st).length;
  const offenerWert = angebote.filter((q) => q.status === 'sent').reduce((s, q) => s + (q.netCents || 0), 0);

  return (
    <div className="page-container-wide">
      <SalesProcessBar stepId="angebote" />
      <PageHeader
        title="Angebote"
        subtitle="Leistungskatalog, Positionen, PDF. Aus dem angenommenen Angebot wird der Auftrag, daraus die Rechnung."
        actions={(
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setZeigeKatalog(true)}>
              <Icon name="template" size={14} /> Leistungskatalog
            </button>
            {/* POST /angebote/abgelaufene schreibt Status: Router-Schreibrecht angebot.bearbeiten (Welle 2). */}
            {darfBearbeiten && <button type="button" className="btn btn-secondary" onClick={abgelaufene}>Abgelaufene prüfen</button>}
            {darfBearbeiten && (
              <button type="button" className="btn btn-primary" onClick={() => setZeigeNeu((v) => !v)}>
                <Icon name="plus" size={14} /> Neues Angebot
              </button>
            )}
          </>
        )}
      />

      {!laedt && !statusFilter && angebote.length > 0 && (
        <KpiRow
          aria-label="Angebots-Kennzahlen"
          items={[
            { label: 'Entwürfe', value: zaehle('draft'), to: '/angebote?status=draft' },
            { label: 'verschickt', value: zaehle('sent'), to: '/angebote?status=sent', hint: euro(offenerWert) },
            { label: 'angenommen', value: zaehle('accepted'), tone: 'good', to: '/angebote?status=accepted' },
            { label: 'abgelehnt', value: zaehle('declined'), to: '/angebote?status=declined' },
            { label: 'abgelaufen', value: zaehle('expired'), tone: zaehle('expired') ? 'warn' : 'neutral', to: '/angebote?status=expired' },
          ]}
        />
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {zeigeNeu && darfBearbeiten && (
        <div className="quick-form" role="form" aria-label="Neues Angebot">
          <label>
            Titel
            <input autoFocus type="text" placeholder="z. B. Pentest Webshop" value={neuTitel} onChange={(e) => setNeuTitel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') anlegen(); }} />
          </label>
          <label>
            Firma
            <select value={neuFirma} onChange={(e) => setNeuFirma(e.target.value)}>
              <option value="">Firma wählen…</option>
              {firmen.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>
          <div className="quick-form__actions">
            <button type="button" className="btn btn-primary" onClick={anlegen}>Anlegen & öffnen</button>
            <button type="button" className="btn btn-ghost" onClick={() => setZeigeNeu(false)}>Abbrechen</button>
          </div>
          <p className="form-helper quick-form__hint">
            Aus der <Link to="/pipeline">Pipeline</Link> heraus angelegte Angebote hängen am Vorgang – bei Annahme wird sein Wert übernommen.
          </p>
        </div>
      )}

      <div className="toolbar">
        <div className="toolbar__group" role="tablist" aria-label="Status">
          <button type="button" role="tab" aria-selected={!statusFilter} className={`btn btn-sm ${!statusFilter ? 'btn-primary' : 'btn-ghost'}`} onClick={() => set('status', null, { replace: true })}>Alle</button>
          {STATUS_REIHENFOLGE.map((st) => (
            <button key={st} type="button" role="tab" aria-selected={statusFilter === st} className={`btn btn-sm ${statusFilter === st ? 'btn-primary' : 'btn-ghost'}`} onClick={() => set('status', st, { replace: true })}>
              {STATUS_LABEL[st]}
            </button>
          ))}
        </div>
        <div className="toolbar__group toolbar__group--right text-sm text-secondary">
          {!laedt && <span>{angebote.length} Angebote</span>}
        </div>
      </div>

      {laedt ? (
        <LoadingState rows={4} label="Angebote werden geladen" />
      ) : angebote.length === 0 ? (
        <EmptyState
          icon="file-check"
          title={statusFilter ? `Kein Angebot mit Status „${STATUS_LABEL[statusFilter] ?? statusFilter}“` : 'Noch kein Angebot'}
          description={statusFilter
            ? 'Anderen Status wählen oder den Filter aufheben.'
            : 'Lege zuerst im Leistungskatalog an, was ihr verkauft – dann geht das Angebot in zwei Klicks. Aus einem Vorgang in der Pipeline heraus ist es schon vorverknüpft.'}
          action={statusFilter
            ? { label: 'Alle Angebote', onClick: () => set('status', null, { replace: true }) }
            : { label: 'Leistungskatalog öffnen', onClick: () => setZeigeKatalog(true) }}
          secondaryAction={darfBearbeiten ? { label: 'Neues Angebot', onClick: () => setZeigeNeu(true) } : undefined}
        />
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Nummer</th><th>Firma</th><th>Titel</th><th>Vorgang</th>
                <th className="r">Netto</th><th>Status</th><th>gültig bis</th>
              </tr>
            </thead>
            <tbody>
              {angebote.map((q) => (
                <tr key={q.id} data-focus-id={q.id} onClick={() => oeffnen(q)} className="tabelle-zeile">
                  <td><strong>{q.number}</strong></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {q.company ? <Link to={`/companies/${q.company.id}`}>{q.company.name}</Link> : '—'}
                  </td>
                  <td>{q.title || '—'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {q.deal ? <Link to={`/pipeline?deal=${q.deal.id}`}>{q.deal.title}</Link> : '—'}
                  </td>
                  <td className="r">{euro(q.netCents, q.currency)}</td>
                  <td><StatusBadge status={q.status}>{STATUS_LABEL[q.status] || q.status}</StatusBadge></td>
                  <td className={q.status === 'expired' ? 'text-error' : ''}>{datum(q.validUntil)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={Boolean(offen)} onOpenChange={(o) => { if (!o) schliessen(); }}>
        {offen && (
          <ModalContent className="detail-modal">
            <ModalHeader>
              <ModalTitle>
                Angebot {offen.number}{' '}
                <StatusBadge status={offen.status}>{STATUS_LABEL[offen.status] || offen.status}</StatusBadge>
              </ModalTitle>
              <ModalDescription>
                {offen.company && <><Link to={`/companies/${offen.company.id}`}>{offen.company.name}</Link> · </>}
                {offen.deal && <>Vorgang <Link to={`/pipeline?deal=${offen.deal.id}`}>{offen.deal.title}</Link> · </>}
                {euro(offen.grossCents, offen.currency)} brutto
              </ModalDescription>
            </ModalHeader>
            <ModalBody>
              <div className="detail-modal__actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => quoteService.pdfOeffnen(offen.id)}>
                  <Icon name="file-text" size={14} /> PDF öffnen
                </button>
                {offen.status === 'draft' && darfVersenden && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => status('sent')}>Als verschickt markieren</button>
                )}
                {(offen.status === 'sent' || offen.status === 'expired') && darfVersenden && (
                  <>
                    <button type="button" className="btn btn-success btn-sm" onClick={() => status('accepted')}>Angenommen</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => status('declined')}>Abgelehnt</button>
                  </>
                )}
                {offen.status === 'accepted' && darfVersenden && !(offen.orders ?? []).some((o) => o.status !== 'cancelled') && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={auftragAnlegen}>Auftrag anlegen</button>
                )}
              </div>
              {((offen.orders ?? []).length > 0 || offen.leadId) && (
                <RelatedLinks
                  className="mt-2"
                  label={`Verknüpft mit Angebot ${offen.number}`}
                  items={[
                    ...(offen.orders ?? []).map((o) => ({
                      key: `order-${o.id}`, label: `Auftrag ${o.number || '(Entwurf)'}`, icon: 'package' as const,
                      to: `/auftraege?auftrag=${encodeURIComponent(o.id)}`, done: o.status !== 'cancelled',
                      title: o.status === 'cancelled' ? 'Stornierter Auftrag' : 'Auftrag öffnen',
                    })),
                    ...(offen.leadId ? [{
                      key: 'lead', label: 'Ansprechpartner', icon: 'user' as const,
                      to: `/lead-sync?lead=${encodeURIComponent(offen.leadId)}`, title: 'Empfänger in Leads & Outreach öffnen',
                    }] : []),
                  ]}
                />
              )}
              {!darfVersenden && offen.status !== 'declined' && (
                <p className="inbox-item-hinweis">Status setzen und Auftrag anlegen ist der Rolle Vertrieb oder Inhaber:in vorbehalten.</p>
              )}

              <div className="akte-felder">
                <label>
                  Titel
                  <input defaultValue={offen.title || ''} disabled={!darfBearbeiten} onBlur={(e) => feld('title', e.target.value)} />
                </label>
                <label>
                  gültig bis
                  <input
                    type="date"
                    defaultValue={isoTag(offen.validUntil)}
                    disabled={!darfBearbeiten}
                    onBlur={(e) => feld('validUntil', e.target.value ? new Date(e.target.value).toISOString() : null)}
                  />
                </label>
                <label>
                  Nachlass gesamt (%)
                  <input type="number" min={0} max={100} defaultValue={offen.discountPercent} disabled={!darfBearbeiten} onBlur={(e) => feld('discountPercent', Number(e.target.value))} />
                </label>
              </div>

              <h3>Positionen</h3>
              <div className="table-container">
                <table className="table tabelle-positionen">
                  <thead>
                    <tr>
                      <th>Leistung</th><th className="r">Menge</th><th>Einheit</th>
                      <th className="r">Einzelpreis</th><th className="r">Rabatt %</th>
                      <th className="r">USt %</th>{darfBearbeiten && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {(offen.items || []).map((it) => (
                      <tr key={it.id}>
                        <td><input aria-label="Leistung" defaultValue={it.description} disabled={!darfBearbeiten} onBlur={(e) => positionAendern(it, { description: e.target.value })} /></td>
                        <td className="r">
                          <input
                            aria-label="Menge"
                            className="schmal" defaultValue={menge(it.quantityHundert)} disabled={!darfBearbeiten}
                            onBlur={(e) => positionAendern(it, { quantityHundert: Math.round((Number(e.target.value.replace(',', '.')) || 0) * 100) })}
                          />
                        </td>
                        <td>
                          <select aria-label="Einheit" defaultValue={it.unit} disabled={!darfBearbeiten} onChange={(e) => positionAendern(it, { unit: e.target.value })}>
                            {EINHEITEN.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </td>
                        <td className="r">
                          <input aria-label="Einzelpreis" className="schmal" defaultValue={(it.unitPriceCents / 100).toFixed(2).replace('.', ',')} disabled={!darfBearbeiten} onBlur={(e) => positionAendern(it, { unitPriceCents: centsAus(e.target.value) })} />
                        </td>
                        <td className="r">
                          <input aria-label="Rabatt" className="sehr-schmal" type="number" min={0} max={100} defaultValue={it.discountPercent} disabled={!darfBearbeiten} onBlur={(e) => positionAendern(it, { discountPercent: Number(e.target.value) })} />
                        </td>
                        <td className="r">
                          <input aria-label="Umsatzsteuer" className="sehr-schmal" type="number" min={0} max={30} defaultValue={it.taxRate} disabled={!darfBearbeiten} onBlur={(e) => positionAendern(it, { taxRate: Number(e.target.value) })} />
                        </td>
                        {darfBearbeiten && <td><button type="button" className="btn btn-sm btn-ghost" onClick={() => positionWeg(it)} aria-label="Position entfernen"><Icon name="trash" size={14} /></button></td>}
                      </tr>
                    ))}
                    {!(offen.items || []).length && (
                      <tr><td colSpan={darfBearbeiten ? 7 : 6} className="text-sm text-secondary">{darfBearbeiten ? 'Noch keine Position – unten aus dem Katalog wählen.' : 'Noch keine Position.'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {darfBearbeiten && (
                <div className="btn-group katalog-zeile">
                  <select className="form-select katalog-select" value="" aria-label="Aus Katalog hinzufügen" onChange={(e) => { if (e.target.value) positionZu(e.target.value); }}>
                    <option value="">Aus Katalog hinzufügen…</option>
                    {produkte.filter((p) => p.active).map((p) => (
                      <option key={p.id} value={p.id}>{p.name} · {euro(p.unitPriceCents)} / {p.unit}</option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => positionZu()}>Freie Position</button>
                </div>
              )}

              <div className="angebot-summe">
                <div><span>Netto</span><strong>{euro(offen.netCents, offen.currency)}</strong></div>
                <div><span>USt</span><strong>{euro(offen.taxCents, offen.currency)}</strong></div>
                <div className="gesamt"><span>Gesamt</span><strong>{euro(offen.grossCents, offen.currency)}</strong></div>
              </div>

              <h3>Anschreiben und Bedingungen</h3>
              <div className="notiz-eingabe">
                <textarea className="form-textarea" rows={3} placeholder="Anschreiben über den Positionen" defaultValue={offen.introText || ''} disabled={!darfBearbeiten} onBlur={(e) => feld('introText', e.target.value)} />
                <textarea className="form-textarea" rows={3} placeholder="Bedingungen unter den Positionen" defaultValue={offen.termsText || ''} disabled={!darfBearbeiten} onBlur={(e) => feld('termsText', e.target.value)} />
              </div>

              <h3>Lebenslauf</h3>
              <p className="inbox-item-hinweis">
                Angelegt {datum(offen.issuedAt)} · verschickt {datum(offen.sentAt)} · angenommen {datum(offen.acceptedAt)}
                {offen.declinedAt && <> · abgelehnt {datum(offen.declinedAt)}{offen.declineReason && `: ${offen.declineReason}`}</>}
              </p>
              {offen.status === 'accepted' && (
                <p className="inbox-item-hinweis">
                  {offen.dealId ? `Der Vorgangswert steht auf ${euro(offen.netCents, offen.currency)}. ` : ''}Mit „Auftrag anlegen“ geht es weiter zu <Link to="/auftraege">Aufträgen</Link> und Rechnung.
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <button type="button" className="btn btn-secondary" onClick={schliessen}>Schließen</button>
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>

      <Modal open={zeigeKatalog} onOpenChange={(o) => { if (!o) setZeigeKatalog(false); }}>
        <ModalContent className="detail-modal">
          <ModalHeader>
            <ModalTitle>Leistungskatalog</ModalTitle>
            <ModalDescription>
              Preise werden beim Hinzufügen zu einem Angebot <strong>kopiert</strong>. Eine spätere Preispflege ändert kein verschicktes Angebot.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {!darfKatalog && <p className="inbox-item-hinweis">Die Katalogpflege ist {rollenAufzaehlung(KATALOG_ROLLEN)} vorbehalten.</p>}
            {darfKatalog && (
              <div className="quick-form" role="form" aria-label="Neue Leistung">
                <label>Bezeichnung<input value={pNeu.name} onChange={(e) => setPNeu({ ...pNeu, name: e.target.value })} /></label>
                <label>
                  Einheit
                  <select value={pNeu.unit} onChange={(e) => setPNeu({ ...pNeu, unit: e.target.value })}>
                    {EINHEITEN.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
                <label>Preis<input placeholder="z. B. 1.400,00" value={pNeu.preis} onChange={(e) => setPNeu({ ...pNeu, preis: e.target.value })} /></label>
                <label>USt %<input type="number" value={pNeu.taxRate} onChange={(e) => setPNeu({ ...pNeu, taxRate: e.target.value })} /></label>
                <div className="quick-form__actions">
                  <button type="button" className="btn btn-primary" onClick={produktAnlegen} disabled={!pNeu.name.trim()}>Anlegen</button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={corePreisliste}
                    title="Pakete, Einrichtung und Zusatzleistungen von SecTepe.Core anlegen oder auf den aktuellen Preis bringen"
                  >
                    Core-Preisliste übernehmen
                  </button>
                </div>
              </div>
            )}
            {produkte.length === 0 ? (
              <EmptyState compact icon="template" title="Noch keine Leistung im Katalog" description="Lege Tagessätze, Pauschalen oder Produkte an – daraus bestehen später die Angebote." />
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead><tr><th>Leistung</th><th>Preis</th><th>USt</th>{darfKatalog && <th />}</tr></thead>
                  <tbody>
                    {produkte.map((p) => (
                      <tr key={p.id} className={p.active ? '' : 'inaktiv'}>
                        <td><strong>{p.name}</strong></td>
                        <td>{euro(p.unitPriceCents, p.currency)} / {p.unit}</td>
                        <td>{p.taxRate} %</td>
                        {darfKatalog && (
                          <td className="r">
                            <button type="button" className="btn btn-sm btn-ghost" onClick={() => produktUmschalten(p)}>
                              {p.active ? 'stilllegen' : 'aktivieren'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default QuotesPage;
